/**
 * 麻将牌素材映射
 *
 * 素材来源：FluffyStuff/riichi-mahjong-tiles（CC0 公共领域）
 *   https://github.com/FluffyStuff/riichi-mahjong-tiles
 *
 * 命名规则（与 34 索引一一对应）：
 *   Man1-9  → 万 1-9        （索引 0-8）
 *   Pin1-9  → 筒 1-9        （索引 9-17）
 *   Sou1-9  → 索 1-9        （索引 18-26）
 *   Ton     → 东            （索引 27）
 *   Nan     → 南            （索引 28）
 *   Shaa    → 西            （索引 29）★ 注意是 Shaa 不是 Sha
 *   Pei     → 北            （索引 30）
 *   Haku    → 白            （索引 31）
 *   Hatsu   → 发            （索引 32）
 *   Chun    → 中            （索引 33）
 *
 * 特殊：
 *   Man5-Dora / Pin5-Dora / Sou5-Dora  → 赤宝牌（红 5）
 *   Back  → 牌背（他家手牌）
 *   Front → 牌面空白
 */

// ════════════════════════════════════════════════════════════════
// 牌 → 素材文件名
// ════════════════════════════════════════════════════════════════

/** 34 种牌的素材名（索引 = 牌索引） */
export const TILE_SPRITE_NAMES: readonly string[] = [
  // 万 0-8
  'Man1', 'Man2', 'Man3', 'Man4', 'Man5', 'Man6', 'Man7', 'Man8', 'Man9',
  // 筒 9-17
  'Pin1', 'Pin2', 'Pin3', 'Pin4', 'Pin5', 'Pin6', 'Pin7', 'Pin8', 'Pin9',
  // 索 18-26
  'Sou1', 'Sou2', 'Sou3', 'Sou4', 'Sou5', 'Sou6', 'Sou7', 'Sou8', 'Sou9',
  // 字牌 27-33
  'Ton', 'Nan', 'Shaa', 'Pei', 'Haku', 'Hatsu', 'Chun',
];

/** 赤宝牌素材名（索引 → 赤牌名） */
export const RED_FIVE_SPRITE_NAMES: Readonly<Record<number, string>> = {
  4: 'Man5-Dora', // 赤 5 万
  13: 'Pin5-Dora', // 赤 5 筒
  22: 'Sou5-Dora', // 赤 5 索
};

/** 特殊素材 */
export const SPECIAL_SPRITES = {
  back: 'Back', // 牌背（他家手牌）
  front: 'Front', // 牌面空白
  blank: 'Blank', // 完全空白
} as const;

// ════════════════════════════════════════════════════════════════
// 尺寸档位
// ════════════════════════════════════════════════════════════════

/** 可选尺寸档 */
export type TileSizeTag = '1x' | '2x' | '3x' | '5x';

/** 各档位像素尺寸（麻将牌比例 3:4）*/
export const TILE_SIZES: Readonly<Record<TileSizeTag, { w: number; h: number }>> = {
  '1x': { w: 60, h: 80 },
  '2x': { w: 120, h: 160 },
  '3x': { w: 180, h: 240 },
  '5x': { w: 600, h: 800 },
};

// ════════════════════════════════════════════════════════════════
// 路径生成
// ════════════════════════════════════════════════════════════════

/** 素材根路径（Vite public 目录）*/
const TILE_BASE = '/tiles';

/**
 * 生成素材 URL
 *
 * @param name 素材名（如 'Man1' / 'Back'）
 * @param size 尺寸档（默认 '2x'）
 */
export function tileSpriteUrl(name: string, size: TileSizeTag = '2x'): string {
  if (size === '5x') {
    // 原图没有后缀
    return `${TILE_BASE}/${name}.png`;
  }
  return `${TILE_BASE}/${name}@${size}.png`;
}

/**
 * 根据牌索引生成素材 URL
 *
 * @param index 牌索引（0-33）
 * @param opts.isRed 是否赤宝牌
 * @param opts.size 尺寸档
 */
export function tileUrlByIndex(
  index: number,
  opts: { isRed?: boolean; size?: TileSizeTag } = {},
): string {
  const { isRed = false, size = '2x' } = opts;

  if (isRed && RED_FIVE_SPRITE_NAMES[index]) {
    return tileSpriteUrl(RED_FIVE_SPRITE_NAMES[index], size);
  }
  const name = TILE_SPRITE_NAMES[index];
  if (!name) {
    console.warn(`未知牌索引: ${index}`);
    return tileSpriteUrl(SPECIAL_SPRITES.blank, size);
  }
  return tileSpriteUrl(name, size);
}

/** 牌背 URL */
export function tileBackUrl(size: TileSizeTag = '2x'): string {
  return tileSpriteUrl(SPECIAL_SPRITES.back, size);
}

/** 空白 URL */
export function tileBlankUrl(size: TileSizeTag = '2x'): string {
  return tileSpriteUrl(SPECIAL_SPRITES.blank, size);
}

// ════════════════════════════════════════════════════════════════
// 反向映射（调试用）
// ════════════════════════════════════════════════════════════════

/** 素材名 → 索引 */
export function spriteNameToIndex(name: string): number | null {
  const i = TILE_SPRITE_NAMES.indexOf(name);
  if (i >= 0) return i;
  // 赤宝牌
  for (const [idx, redName] of Object.entries(RED_FIVE_SPRITE_NAMES)) {
    if (redName === name) return Number(idx);
  }
  return null;
}

/** 牌索引 → 中文名（用于 UI 显示）*/
export const TILE_CHINESE_NAMES: readonly string[] = [
  '一万', '二万', '三万', '四万', '五万', '六万', '七万', '八万', '九万',
  '一筒', '二筒', '三筒', '四筒', '五筒', '六筒', '七筒', '八筒', '九筒',
  '一索', '二索', '三索', '四索', '五索', '六索', '七索', '八索', '九索',
  '东', '南', '西', '北', '白', '发', '中',
];

/** 短中文名（用于紧凑显示）*/
export const TILE_SHORT_NAMES: readonly string[] = [
  '1m', '2m', '3m', '4m', '5m', '6m', '7m', '8m', '9m',
  '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p',
  '1s', '2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s',
  '东', '南', '西', '北', '白', '发', '中',
];
